import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash, X, ArrowUp, ArrowDown, Copy, CaretDown, CaretRight, Eye } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import ConfirmDeleteDialog from '../ConfirmDeleteDialog';
import { QuestionnairePreviewDialog } from '../study/QuestionnairePreviewDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DEFAULT_PREAMBLE = 'Tras el uso del producto durante el periodo indicado, valore su grado de acuerdo con las siguientes afirmaciones marcando una de las 5 opciones (1 = Totalmente en desacuerdo, 5 = Totalmente de acuerdo).';
const DEFAULT_PREAMBLE_7 = 'Tras el uso del producto durante el periodo indicado, valore las siguientes afirmaciones marcando una de las 7 opciones (1 = Me disgusta mucho, 7 = Me gusta mucho).';

/**
 * Reusable Satisfaction (Likert 1-5) Questionnaires — URS-F210.
 * Templates live at config level and can be imported into any study.
 */
export function SatisfactionQuestionnairesPanel({ lang }) {
  const [qs, setQs] = useState([]);
  const [areas, setAreas] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [saving, setSaving] = useState(false);

  const blankForm = {
    name: '',
    satisfaction_meta: {
      area_name: '',
      subarea_name: '',
      family_name: '',
      claim_principal: '',
      parametros_clave: [],
      periodo_uso: '',
    },
    preamble_es: DEFAULT_PREAMBLE,
    questions: [],
  };
  const [form, setForm] = useState(blankForm);
  const [paramTxt, setParamTxt] = useState('');
  const [newQText, setNewQText] = useState('');
  const [expandedFollowUps, setExpandedFollowUps] = useState({}); // idx -> bool
  const [duplicating, setDuplicating] = useState(null);
  // URS-F353: vista previa del cuestionario de satisfacción.
  const [previewQ, setPreviewQ] = useState(null);

  const load = () => {
    Promise.all([
      axios.get(`${API_URL}/api/config/satisfaction-questionnaires`, { withCredentials: true }),
      axios.get(`${API_URL}/api/config/satisfaction-areas`, { withCredentials: true }),
    ]).then(([qr, ar]) => {
      setQs(qr.data || []);
      setAreas(ar.data || []);
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(JSON.parse(JSON.stringify(blankForm)));
    setExpandedFollowUps({});
    setShowDialog(true);
  };
  const openEdit = (q) => {
    setEditing(q);
    setForm({
      name: q.name,
      satisfaction_meta: {
        area_name: '', subarea_name: '', family_name: '',
        claim_principal: '', parametros_clave: [], periodo_uso: '',
        ...(q.satisfaction_meta || {}),
      },
      preamble_es: q.preamble_es || DEFAULT_PREAMBLE,
      questions: (q.questions || []).map(qq => ({
        ...qq,
        scale_size: qq.scale_size === 7 ? 7 : 5,
        follow_up_texts: Array.isArray(qq.follow_up_texts) ? qq.follow_up_texts.map(ft => ({ ...ft })) : [],
      })),
    });
    setExpandedFollowUps({});
    setShowDialog(true);
  };

  // ---- Chained selectors derived from areas catalog ----
  const selectedArea = useMemo(
    () => areas.find(a => a.name === form.satisfaction_meta.area_name),
    [areas, form.satisfaction_meta.area_name],
  );
  const selectedSubarea = useMemo(
    () => selectedArea?.subareas?.find(s => s.name === form.satisfaction_meta.subarea_name),
    [selectedArea, form.satisfaction_meta.subarea_name],
  );
  const selectedFamily = useMemo(
    () => selectedSubarea?.families?.find(f => f.name === form.satisfaction_meta.family_name),
    [selectedSubarea, form.satisfaction_meta.family_name],
  );

  const updateMeta = (key, val) => setForm(p => ({ ...p, satisfaction_meta: { ...p.satisfaction_meta, [key]: val } }));

  // When family changes, suggest parameters
  const handleFamilyChange = (familyName) => {
    setForm(p => {
      const fam = selectedSubarea?.families?.find(f => f.name === familyName);
      const suggested = fam?.suggested_parameters || [];
      // Merge suggested only if user has none yet
      const currentParams = p.satisfaction_meta.parametros_clave || [];
      const merged = currentParams.length === 0 ? suggested : currentParams;
      return {
        ...p,
        satisfaction_meta: { ...p.satisfaction_meta, family_name: familyName, parametros_clave: merged },
      };
    });
  };

  const addParam = () => {
    const txt = paramTxt.trim();
    if (!txt) return;
    if ((form.satisfaction_meta.parametros_clave || []).includes(txt)) { setParamTxt(''); return; }
    updateMeta('parametros_clave', [...(form.satisfaction_meta.parametros_clave || []), txt]);
    setParamTxt('');
  };
  const removeParam = (idx) => updateMeta(
    'parametros_clave',
    (form.satisfaction_meta.parametros_clave || []).filter((_, i) => i !== idx),
  );

  const addSuggested = (p) => {
    if ((form.satisfaction_meta.parametros_clave || []).includes(p)) return;
    updateMeta('parametros_clave', [...(form.satisfaction_meta.parametros_clave || []), p]);
  };

  // ---- Questions ----
  const addQuestion = () => {
    const txt = newQText.trim();
    if (!txt) return;
    setForm(p => ({
      ...p,
      questions: [...p.questions, { text_es: txt, required: true, type: 'likert', scale_size: 5, follow_up_texts: [], order: p.questions.length }],
    }));
    setNewQText('');
  };
  const addSectionHeader = () => {
    setForm(p => ({
      ...p,
      questions: [...p.questions, { type: 'section_header', title_es: '', order: p.questions.length }],
    }));
  };
  const addFreeTextQuestion = () => {
    setForm(p => ({
      ...p,
      questions: [...p.questions, { type: 'free_text', text_es: '', required: false, max_chars: 1000, order: p.questions.length }],
    }));
  };
  const updateQuestion = (idx, key, val) => setForm(p => ({
    ...p,
    questions: p.questions.map((q, i) => i === idx ? { ...q, [key]: val } : q),
  }));
  const removeQuestion = (idx) => setForm(p => ({ ...p, questions: p.questions.filter((_, i) => i !== idx) }));
  const moveQuestion = (idx, delta) => setForm(p => {
    const j = idx + delta;
    if (j < 0 || j >= p.questions.length) return p;
    const arr = [...p.questions];
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...p, questions: arr };
  });

  // ---- Follow-up free-text per question ----
  const toggleFollowUps = (idx) => setExpandedFollowUps(p => ({ ...p, [idx]: !p[idx] }));
  const addFollowUp = (qIdx) => setForm(p => ({
    ...p,
    questions: p.questions.map((q, i) => i === qIdx ? {
      ...q,
      follow_up_texts: [
        ...(q.follow_up_texts || []),
        { text_es: '', required: false, only_if_low: false, max_chars: 500, order: (q.follow_up_texts || []).length },
      ],
    } : q),
  }));
  const updateFollowUp = (qIdx, fIdx, key, val) => setForm(p => ({
    ...p,
    questions: p.questions.map((q, i) => i === qIdx ? {
      ...q,
      follow_up_texts: (q.follow_up_texts || []).map((ft, j) => j === fIdx ? { ...ft, [key]: val } : ft),
    } : q),
  }));
  const removeFollowUp = (qIdx, fIdx) => setForm(p => ({
    ...p,
    questions: p.questions.map((q, i) => i === qIdx ? {
      ...q,
      follow_up_texts: (q.follow_up_texts || []).filter((_, j) => j !== fIdx),
    } : q),
  }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error(lang === 'es' ? 'El nombre es obligatorio' : 'Name required'); return; }
    const answerable = form.questions.filter(q => q.type !== 'section_header');
    if (answerable.length === 0) { toast.error(lang === 'es' ? 'Añade al menos una pregunta (Likert o abierta)' : 'Add at least one question (Likert or open)'); return; }
    const emptySection = form.questions.find(q => q.type === 'section_header' && !(q.title_es || '').trim());
    if (emptySection) { toast.error(lang === 'es' ? 'Hay un título de sección vacío' : 'There is an empty section title'); return; }
    const emptyFreeText = form.questions.find(q => q.type === 'free_text' && !(q.text_es || '').trim());
    if (emptyFreeText) { toast.error(lang === 'es' ? 'Hay una pregunta abierta sin enunciado' : 'There is an empty open question'); return; }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/config/satisfaction-questionnaires/${editing.questionnaire_id}`, form, { withCredentials: true });
        toast.success(lang === 'es' ? 'Cuestionario actualizado' : 'Updated');
      } else {
        await axios.post(`${API_URL}/api/config/satisfaction-questionnaires`, form, { withCredentials: true });
        toast.success(lang === 'es' ? 'Cuestionario creado' : 'Created');
      }
      setShowDialog(false); load();
    } catch (e) { toast.error(errText(e, 'Error')); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/config/satisfaction-questionnaires/${id}`, { withCredentials: true });
      toast.success(lang === 'es' ? 'Eliminado' : 'Deleted');
      setConfirmDel(null); load();
    } catch (e) { toast.error(errText(e, 'Error')); }
  };

  const handleDuplicate = async (id) => {
    setDuplicating(id);
    try {
      await axios.post(`${API_URL}/api/config/satisfaction-questionnaires/${id}/duplicate`, {}, { withCredentials: true });
      toast.success(lang === 'es' ? 'Cuestionario duplicado' : 'Questionnaire duplicated');
      load();
    } catch (e) { toast.error(errText(e, 'Error')); }
    setDuplicating(null);
  };

  return (
    <div className="space-y-4" data-testid="satisfaction-questionnaires-panel">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs text-stone-500 max-w-2xl">
          {lang === 'es'
            ? 'Cuestionarios de satisfacción subjetiva (escala Likert 1-5 o 1-7, combinables) reutilizables entre estudios. Una vez creados aquí, se importan desde la pestaña Cuestionarios del estudio.'
            : 'Reusable subjective satisfaction questionnaires (Likert 1-5 or 1-7, mixable). Created here and imported per study.'}
        </p>
        <Button onClick={openCreate} data-testid="sat-q-new-btn" className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2">
          <Plus size={16} />{lang === 'es' ? 'Nuevo cuestionario' : 'New questionnaire'}
        </Button>
      </div>

      {qs.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-sm p-10 text-center text-sm text-stone-500">
          {lang === 'es' ? 'Sin cuestionarios de satisfacción configurados' : 'No satisfaction questionnaires yet'}
        </div>
      ) : (
        <div className="space-y-3">
          {qs.map(q => {
            const m = q.satisfaction_meta || {};
            return (
              <div key={q.questionnaire_id} data-testid={`sat-q-card-${q.questionnaire_id}`} className="bg-white border border-stone-200 rounded-sm p-4">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <h3 className="text-sm font-semibold text-stone-900">{q.name}</h3>
                      {(() => {
                        const sizes = Array.from(new Set((q.questions || []).map(qq => qq.scale_size === 7 ? 7 : 5)));
                        const label = sizes.length === 0 ? 'Likert 1-5' : sizes.length === 1 ? `Likert 1-${sizes[0]}` : 'Likert mixto';
                        return <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">{label}</Badge>;
                      })()}
                      <Badge variant="outline" className="text-[10px] font-mono">{(q.questions || []).filter(qq => qq.type !== 'section_header').length} {lang === 'es' ? 'items' : 'items'}</Badge>
                      {(q.questions || []).some(qq => qq.type === 'section_header') && (
                        <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">{(q.questions || []).filter(qq => qq.type === 'section_header').length} {lang === 'es' ? 'secciones' : 'sections'}</Badge>
                      )}
                      {(q.questions || []).some(qq => (qq.follow_up_texts || []).length > 0) && (
                        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">+ texto libre</Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5 text-[11px] text-stone-600">
                      {m.area_name && <span><b>Área:</b> {m.area_name}</span>}
                      {m.subarea_name && <span>· <b>Subárea:</b> {m.subarea_name}</span>}
                      {m.family_name && <span>· <b>Familia:</b> {m.family_name}</span>}
                      {m.periodo_uso && <span>· <b>Periodo:</b> {m.periodo_uso}</span>}
                    </div>
                    {m.claim_principal && (
                      <div className="text-[11px] text-stone-500 mt-1"><b>Claim:</b> {m.claim_principal}</div>
                    )}
                    {(m.parametros_clave || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {(m.parametros_clave || []).map((p, i) => (
                          <span key={i} className="bg-violet-50 text-violet-700 border border-violet-200 rounded-full px-2 text-[10px]">{p}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-500 hover:text-[#1f3e80]" onClick={() => setPreviewQ(q)} data-testid={`sat-q-preview-${q.questionnaire_id}`} title={lang === 'es' ? 'Vista previa' : 'Preview'}><Eye size={13} /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(q)} data-testid={`sat-q-edit-${q.questionnaire_id}`}><Pencil size={13} /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-stone-600" disabled={duplicating === q.questionnaire_id} onClick={() => handleDuplicate(q.questionnaire_id)} data-testid={`sat-q-duplicate-${q.questionnaire_id}`} title={lang === 'es' ? 'Duplicar cuestionario' : 'Duplicate questionnaire'}><Copy size={13} /></Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => setConfirmDel(q)} data-testid={`sat-q-delete-${q.questionnaire_id}`}><Trash size={13} /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>{editing ? (lang === 'es' ? 'Editar cuestionario de satisfacción' : 'Edit satisfaction questionnaire') : (lang === 'es' ? 'Nuevo cuestionario de satisfacción' : 'New satisfaction questionnaire')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 overflow-y-auto flex-1 pr-1">
            {/* Nombre */}
            <div className="space-y-1.5">
              <Label>{lang === 'es' ? 'Nombre del cuestionario' : 'Questionnaire name'} <span className="text-red-500">*</span></Label>
              <Input data-testid="sat-q-name-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder={lang === 'es' ? 'Ej: Satisfacción crema hidratante 14 días' : 'e.g. Satisfaction moisturizer 14d'} />
            </div>

            {/* Metadatos */}
            <div className="bg-stone-50 border border-stone-200 rounded-sm p-3">
              <Label className="text-xs font-semibold text-stone-600 uppercase tracking-wider">{lang === 'es' ? 'Metadatos regulatorios' : 'Regulatory metadata'}</Label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">{lang === 'es' ? 'Área funcional' : 'Functional area'}</Label>
                  <Select value={form.satisfaction_meta.area_name || '_'} onValueChange={v => setForm(p => ({ ...p, satisfaction_meta: { ...p.satisfaction_meta, area_name: v === '_' ? '' : v, subarea_name: '', family_name: '', parametros_clave: [] } }))}>
                    <SelectTrigger data-testid="sat-q-area-select" className="text-xs"><SelectValue placeholder={lang === 'es' ? 'Seleccionar...' : 'Select...'} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_">{lang === 'es' ? '— Sin definir —' : '— Not set —'}</SelectItem>
                      {areas.map(a => <SelectItem key={a.area_id} value={a.name}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{lang === 'es' ? 'Subárea' : 'Subarea'}</Label>
                  <Select value={form.satisfaction_meta.subarea_name || '_'} disabled={!selectedArea} onValueChange={v => setForm(p => ({ ...p, satisfaction_meta: { ...p.satisfaction_meta, subarea_name: v === '_' ? '' : v, family_name: '', parametros_clave: [] } }))}>
                    <SelectTrigger data-testid="sat-q-subarea-select" className="text-xs"><SelectValue placeholder={lang === 'es' ? 'Seleccionar...' : 'Select...'} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_">{lang === 'es' ? '— Sin definir —' : '— Not set —'}</SelectItem>
                      {(selectedArea?.subareas || []).map(s => <SelectItem key={s.subarea_id} value={s.name}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{lang === 'es' ? 'Familia de producto' : 'Product family'}</Label>
                  <Select value={form.satisfaction_meta.family_name || '_'} disabled={!selectedSubarea} onValueChange={v => handleFamilyChange(v === '_' ? '' : v)}>
                    <SelectTrigger data-testid="sat-q-family-select" className="text-xs"><SelectValue placeholder={lang === 'es' ? 'Seleccionar...' : 'Select...'} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_">{lang === 'es' ? '— Sin definir —' : '— Not set —'}</SelectItem>
                      {(selectedSubarea?.families || []).map(f => <SelectItem key={f.family_id} value={f.name}>{f.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">{lang === 'es' ? 'Claim principal' : 'Main claim'}</Label>
                  <Input data-testid="sat-q-claim-input" className="text-xs" value={form.satisfaction_meta.claim_principal} onChange={e => updateMeta('claim_principal', e.target.value)} placeholder={lang === 'es' ? 'Ej: Hidrata 24 h' : 'e.g. Hydrates 24h'} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">{lang === 'es' ? 'Periodo de uso' : 'Usage period'}</Label>
                  <Input data-testid="sat-q-period-input" className="text-xs" value={form.satisfaction_meta.periodo_uso} onChange={e => updateMeta('periodo_uso', e.target.value)} placeholder={lang === 'es' ? 'Ej: 14 días' : 'e.g. 14 days'} />
                </div>
              </div>

              {/* Parámetros clave */}
              <div className="mt-3 space-y-1.5">
                <Label className="text-xs">{lang === 'es' ? 'Parámetros clave' : 'Key parameters'}</Label>
                <div className="flex flex-wrap gap-1 min-h-[28px]">
                  {(form.satisfaction_meta.parametros_clave || []).map((p, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-violet-50 text-violet-700 border border-violet-200 rounded-full px-2 py-0.5 text-[11px]">
                      {p}
                      <button type="button" onClick={() => removeParam(i)} className="hover:text-red-600"><X size={10} /></button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-1">
                  <Input
                    data-testid="sat-q-param-input"
                    value={paramTxt}
                    onChange={e => setParamTxt(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addParam(); } }}
                    placeholder={lang === 'es' ? 'Escribe parámetro y pulsa Enter' : 'Type parameter and press Enter'}
                    className="text-xs h-8"
                  />
                  <Button variant="outline" size="sm" onClick={addParam} className="h-8 px-2"><Plus size={12} /></Button>
                </div>
                {selectedFamily && (selectedFamily.suggested_parameters || []).length > 0 && (
                  <div className="mt-1.5">
                    <span className="text-[10px] text-stone-400">{lang === 'es' ? 'Sugeridos por la familia:' : 'Suggested by family:'}</span>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {(selectedFamily.suggested_parameters || []).filter(p => !(form.satisfaction_meta.parametros_clave || []).includes(p)).map((p, i) => (
                        <button key={i} type="button" onClick={() => addSuggested(p)} className="bg-stone-100 hover:bg-violet-50 text-stone-600 hover:text-violet-700 border border-stone-200 hover:border-violet-200 rounded-full px-2 text-[10px] transition">+ {p}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Preámbulo */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label>{lang === 'es' ? 'Preámbulo (mostrado al voluntario)' : 'Preamble (shown to volunteer)'}</Label>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" type="button" className="text-[11px] h-6 px-2" onClick={() => setForm(p => ({ ...p, preamble_es: DEFAULT_PREAMBLE }))}>{lang === 'es' ? 'Plantilla 1-5' : 'Default 1-5'}</Button>
                  <Button variant="ghost" size="sm" type="button" className="text-[11px] h-6 px-2" onClick={() => setForm(p => ({ ...p, preamble_es: DEFAULT_PREAMBLE_7 }))}>{lang === 'es' ? 'Plantilla 1-7' : 'Default 1-7'}</Button>
                </div>
              </div>
              <Textarea data-testid="sat-q-preamble-input" value={form.preamble_es} onChange={e => setForm(p => ({ ...p, preamble_es: e.target.value }))} className="min-h-[80px] text-sm" />
            </div>

            {/* Preguntas */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{lang === 'es' ? 'Afirmaciones (escala Likert)' : 'Statements (Likert)'} <span className="text-stone-400 text-xs">({form.questions.filter(q => q.type !== 'section_header').length})</span></Label>
                <span className="text-[10px] text-stone-400">{lang === 'es' ? 'Escalas mixtas 1-5 / 1-7 + títulos de sección admitidos.' : 'Mixed 1-5 / 1-7 scales + section titles allowed.'}</span>
              </div>
              <div className="flex gap-1 flex-wrap">
                <Input
                  data-testid="sat-q-new-question-input"
                  value={newQText}
                  onChange={e => setNewQText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addQuestion(); } }}
                  placeholder={lang === 'es' ? 'Ej: El producto hidrata mi piel' : 'e.g. The product hydrates my skin'}
                  className="text-sm min-w-[180px] flex-1"
                />
                <Button variant="outline" data-testid="sat-q-add-question-btn" onClick={addQuestion} className="gap-1 rounded-sm shrink-0"><Plus size={12} />{lang === 'es' ? 'Añadir Likert' : 'Add Likert'}</Button>
                <Button variant="outline" data-testid="sat-q-add-freetext-btn" onClick={addFreeTextQuestion} className="gap-1 rounded-sm shrink-0 border-amber-300 text-amber-700 hover:bg-amber-50" title={lang === 'es' ? 'Añadir una pregunta abierta de texto libre (independiente)' : 'Add an independent free-text question'}><Plus size={12} />{lang === 'es' ? 'Pregunta abierta' : 'Open question'}</Button>
                <Button variant="outline" data-testid="sat-q-add-section-btn" onClick={addSectionHeader} className="gap-1 rounded-sm shrink-0 border-violet-300 text-violet-700 hover:bg-violet-50" title={lang === 'es' ? 'Añadir un título para agrupar las afirmaciones que vengan debajo' : 'Add a section title'}><Plus size={12} />{lang === 'es' ? 'Título de sección' : 'Section title'}</Button>
              </div>
              {form.questions.length === 0 ? (
                <p className="text-xs text-stone-400 text-center py-4">{lang === 'es' ? 'Sin afirmaciones' : 'No statements yet'}</p>
              ) : (
                <div className="space-y-2">
                  {(() => {
                    let likertCount = 0;
                    return form.questions.map((q, idx) => {
                    // URS-F263: Section header item
                    if (q.type === 'section_header') {
                      return (
                        <div key={idx} className="bg-violet-50 border border-violet-200 rounded-sm" data-testid={`sat-q-section-${idx}`}>
                          <div className="flex items-center gap-2 px-2.5 py-1.5">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-violet-700 shrink-0">{lang === 'es' ? 'Sección' : 'Section'}</span>
                            <Input
                              value={q.title_es || ''}
                              onChange={e => updateQuestion(idx, 'title_es', e.target.value)}
                              placeholder={lang === 'es' ? 'Título de sección (ej: Aroma y textura)' : 'Section title (e.g. Scent and texture)'}
                              className="text-sm h-8 border-0 focus-visible:ring-1 px-2 flex-1 bg-white font-semibold text-violet-900"
                              data-testid={`sat-q-section-title-${idx}`}
                            />
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveQuestion(idx, -1)} disabled={idx === 0}><ArrowUp size={11} /></Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveQuestion(idx, 1)} disabled={idx === form.questions.length - 1}><ArrowDown size={11} /></Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => removeQuestion(idx)} data-testid={`sat-q-section-remove-${idx}`}><X size={12} /></Button>
                          </div>
                        </div>
                      );
                    }
                    // URS-F266: pregunta abierta independiente (texto libre intercalado)
                    if (q.type === 'free_text') {
                      likertCount += 1;
                      const displayNum = likertCount;
                      return (
                        <div key={idx} className="bg-amber-50/40 border border-amber-200 rounded-sm" data-testid={`sat-q-freetext-${idx}`}>
                          <div className="flex items-center gap-2 px-2.5 py-1.5">
                            <span className="font-mono text-xs text-stone-500 w-6">{displayNum}.</span>
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-700 shrink-0">{lang === 'es' ? 'Abierta' : 'Open'}</span>
                            <Input
                              value={q.text_es || ''}
                              onChange={e => updateQuestion(idx, 'text_es', e.target.value)}
                              placeholder={lang === 'es' ? 'Ej: ¿Qué es lo que más te ha gustado del producto?' : 'e.g. What did you like most about the product?'}
                              className="text-sm h-8 border-0 focus-visible:ring-1 px-2 flex-1 bg-white"
                              data-testid={`sat-q-freetext-text-${idx}`}
                            />
                            <label className="flex items-center gap-1 text-[11px] text-stone-500">
                              <input type="checkbox" checked={!!q.required} onChange={e => updateQuestion(idx, 'required', e.target.checked)} className="accent-[#1f3e80]" />
                              {lang === 'es' ? 'Obl.' : 'Req.'}
                            </label>
                            <label className="flex items-center gap-1 text-[11px] text-stone-500">
                              {lang === 'es' ? 'Máx:' : 'Max:'}
                              <Input
                                type="number"
                                min={50}
                                max={5000}
                                value={q.max_chars || 1000}
                                onChange={e => updateQuestion(idx, 'max_chars', parseInt(e.target.value || '1000', 10))}
                                className="h-7 w-16 px-1 text-[11px]"
                                data-testid={`sat-q-freetext-max-${idx}`}
                              />
                            </label>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveQuestion(idx, -1)} disabled={idx === 0}><ArrowUp size={11} /></Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveQuestion(idx, 1)} disabled={idx === form.questions.length - 1}><ArrowDown size={11} /></Button>
                            <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => removeQuestion(idx)} data-testid={`sat-q-freetext-remove-${idx}`}><X size={12} /></Button>
                          </div>
                        </div>
                      );
                    }
                    const fus = q.follow_up_texts || [];
                    const expanded = !!expandedFollowUps[idx];
                    likertCount += 1;
                    const displayNum = likertCount;
                    return (
                      <div key={idx} className="bg-white border border-stone-200 rounded-sm" data-testid={`sat-q-item-${idx}`}>
                        <div className="flex items-center gap-2 px-2.5 py-1.5">
                          <span className="font-mono text-xs text-stone-400 w-6">{displayNum}.</span>
                          <Input
                            value={q.text_es}
                            onChange={e => updateQuestion(idx, 'text_es', e.target.value)}
                            className="text-sm h-8 border-0 focus-visible:ring-1 px-2 flex-1"
                          />
                          <Select value={String(q.scale_size === 7 ? 7 : 5)} onValueChange={v => updateQuestion(idx, 'scale_size', parseInt(v, 10))}>
                            <SelectTrigger data-testid={`sat-q-scale-${idx}`} className="h-8 w-[88px] text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="5">1 – 5</SelectItem>
                              <SelectItem value="7">1 – 7</SelectItem>
                            </SelectContent>
                          </Select>
                          <label className="flex items-center gap-1 text-[11px] text-stone-500">
                            <input type="checkbox" checked={!!q.required} onChange={e => updateQuestion(idx, 'required', e.target.checked)} className="accent-[#1f3e80]" />
                            {lang === 'es' ? 'Obl.' : 'Req.'}
                          </label>
                          <Button
                            variant="outline"
                            size="sm"
                            type="button"
                            className={`h-7 px-2 text-[11px] gap-1 shrink-0 ${fus.length > 0 ? 'border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100' : 'border-stone-300 text-stone-600 hover:bg-stone-50'}`}
                            onClick={() => toggleFollowUps(idx)}
                            data-testid={`sat-q-followups-toggle-${idx}`}
                            title={lang === 'es' ? 'Añadir preguntas de texto libre debajo de esta afirmación' : 'Add free-text follow-up questions below this statement'}
                          >
                            {expanded ? <CaretDown size={11} /> : <CaretRight size={11} />}
                            {lang === 'es' ? 'Texto libre' : 'Free text'}
                            {fus.length > 0 && <span className="ml-0.5 font-mono">({fus.length})</span>}
                          </Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveQuestion(idx, -1)} disabled={idx === 0}><ArrowUp size={11} /></Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => moveQuestion(idx, 1)} disabled={idx === form.questions.length - 1}><ArrowDown size={11} /></Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => removeQuestion(idx)}><X size={12} /></Button>
                        </div>
                        {expanded && (
                          <div className="border-t border-stone-100 bg-stone-50/60 px-3 py-2 space-y-1.5" data-testid={`sat-q-followups-${idx}`}>
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] font-medium text-stone-600">
                                {lang === 'es' ? 'Preguntas abiertas (texto libre) bajo esta afirmación' : 'Open follow-up questions under this statement'}
                              </span>
                              <Button variant="outline" size="sm" className="h-6 px-2 text-[11px] gap-1" onClick={() => addFollowUp(idx)} data-testid={`sat-q-add-followup-${idx}`}>
                                <Plus size={10} /> {lang === 'es' ? 'Añadir' : 'Add'}
                              </Button>
                            </div>
                            {fus.length === 0 ? (
                              <p className="text-[11px] text-stone-400">{lang === 'es' ? 'Sin preguntas abiertas asociadas (opcional).' : 'No follow-up questions (optional).'}</p>
                            ) : (
                              <div className="space-y-1.5">
                                {fus.map((ft, j) => (
                                  <div key={j} className="bg-white border border-stone-200 rounded-sm px-2 py-1.5 flex flex-col gap-1.5" data-testid={`sat-q-followup-${idx}-${j}`}>
                                    <div className="flex items-center gap-1.5">
                                      <span className="font-mono text-[10px] text-stone-400 w-6">{displayNum}.{j + 1}</span>
                                      <Input
                                        value={ft.text_es || ''}
                                        onChange={e => updateFollowUp(idx, j, 'text_es', e.target.value)}
                                        placeholder={lang === 'es' ? '¿Por qué? Cuéntanos más...' : 'Why? Tell us more...'}
                                        className="text-sm h-7 px-2 flex-1"
                                      />
                                      <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => removeFollowUp(idx, j)}><X size={11} /></Button>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-3 pl-7 text-[11px] text-stone-500">
                                      <label className="flex items-center gap-1">
                                        <input type="checkbox" checked={!!ft.required} onChange={e => updateFollowUp(idx, j, 'required', e.target.checked)} className="accent-[#1f3e80]" />
                                        {lang === 'es' ? 'Obligatoria' : 'Required'}
                                      </label>
                                      <label className="flex items-center gap-1" title={lang === 'es' ? 'Solo se muestra/exige cuando la respuesta Likert es 1 o 2 (baja)' : 'Only shown when Likert answer is 1 or 2 (low)'}>
                                        <input type="checkbox" checked={!!ft.only_if_low} onChange={e => updateFollowUp(idx, j, 'only_if_low', e.target.checked)} className="accent-[#1f3e80]" />
                                        {lang === 'es' ? 'Solo si valoración baja (1-2)' : 'Only if low score (1-2)'}
                                      </label>
                                      <label className="flex items-center gap-1">
                                        {lang === 'es' ? 'Máx. caracteres:' : 'Max chars:'}
                                        <Input
                                          type="number"
                                          min={50}
                                          max={5000}
                                          value={ft.max_chars || 500}
                                          onChange={e => updateFollowUp(idx, j, 'max_chars', parseInt(e.target.value || '500', 10))}
                                          className="h-6 w-16 px-1 text-[11px]"
                                        />
                                      </label>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  });
                  })()}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDialog(false)}>{lang === 'es' ? 'Cancelar' : 'Cancel'}</Button>
            <Button data-testid="sat-q-save-btn" onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white">{saving ? '...' : (lang === 'es' ? 'Guardar' : 'Save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!confirmDel}
        onOpenChange={v => { if (!v) setConfirmDel(null); }}
        title={lang === 'es' ? 'Eliminar cuestionario' : 'Delete questionnaire'}
        description={lang === 'es' ? `¿Eliminar "${confirmDel?.name || ''}"?` : `Delete "${confirmDel?.name || ''}"?`}
        onConfirm={() => handleDelete(confirmDel?.questionnaire_id)}
      />
      <QuestionnairePreviewDialog
        open={!!previewQ}
        onClose={() => setPreviewQ(null)}
        questionnaire={previewQ}
        lang={lang}
      />
    </div>
  );
}
